import React, { useEffect, useState } from 'react';
import { useChatWindow, useMessages } from 'react-chatbotify';
import CaRTBotAgent from '@/assets/images/cart_bot_agent.png';
import styles from './ChatBotAI.module.scss';

type DraggableChatButtonProps = {
  userId: string;
};

const DraggableChatButton: React.FC<DraggableChatButtonProps> = (props) => {
  const { userId } = props;
  const { isChatWindowOpen, toggleChatWindow } = useChatWindow();
  const { messages } = useMessages();

  const [unreadCount, setUnreadCount] = useState(1);
  const [position, setPosition] = useState({ x: 24, y: 24 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [wasDragging, setWasDragging] = useState(false);
  const [animate, setAnimate] = useState(false);

  const chatHistoryKey = `cart_chat_history_${userId}`;
  const bubbleSize = 72;
  const margin = 18;

  useEffect(() => {
    setPosition({
      x: window.innerWidth - bubbleSize - margin,
      y: window.innerHeight - bubbleSize - margin,
    });
  }, []);

  useEffect(() => {
    const hasHistory = !!localStorage.getItem(chatHistoryKey);

    if (!hasHistory) {
      setUnreadCount(1);
    }
  }, [chatHistoryKey]);

  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg) return;

    if (lastMsg.sender === 'bot' && !isChatWindowOpen) {
      setUnreadCount((c) => c + 1);
    }
  }, [messages, isChatWindowOpen]);

  useEffect(() => {
    if (isChatWindowOpen) {
      setUnreadCount(0);
    }
  }, [isChatWindowOpen]);

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setDragging(true);
    setWasDragging(false);
    setAnimate(false);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      setWasDragging(true);

      const nextX = e.clientX - offset.x;
      const nextY = e.clientY - offset.y;

      const maxX = window.innerWidth - bubbleSize - margin;
      const maxY = window.innerHeight - bubbleSize - margin;

      setPosition({
        x: Math.min(Math.max(margin, nextX), maxX),
        y: Math.min(Math.max(margin, nextY), maxY),
      });
    };

    const handleMouseUp = () => {
      setDragging(false);
      setAnimate(true);

      setPosition((prev) => {
        const centerX = prev.x + bubbleSize / 2;
        const centerY = prev.y + bubbleSize / 2;
        const width = window.innerWidth;
        const height = window.innerHeight;

        const distLeft = centerX;
        const distRight = width - centerX;
        const distTop = centerY;
        const distBottom = height - centerY;

        const minDist = Math.min(distLeft, distRight, distTop, distBottom);

        const maxX = width - bubbleSize - margin;
        const maxY = height - bubbleSize - margin;

        let { x, y } = prev;
        const minMarginX = Math.min(Math.max(margin, prev.x), maxX);
        const minMarginY = Math.min(Math.max(margin, prev.y), maxY);

        if (minDist === distLeft) {
          x = margin;
          y = minMarginY;
        } else if (minDist === distRight) {
          x = maxX;
          y = minMarginY;
        } else if (minDist === distTop) {
          y = margin;
          x = minMarginX;
        } else {
          y = maxY;
          x = minMarginX;
        }

        return { x, y };
      });

      setTimeout(() => setAnimate(false), 250);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, offset]);

  useEffect(() => {
    const handleResize = () => {
      setAnimate(false);

      setPosition((prev) => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const maxX = width - bubbleSize - margin;
        const maxY = height - bubbleSize - margin;

        let x = Math.min(Math.max(margin, prev.x), maxX);
        let y = Math.min(Math.max(margin, prev.y), maxY);

        const centerX = x + bubbleSize / 2;
        const centerY = y + bubbleSize / 2;

        const distLeft = centerX;
        const distRight = width - centerX;
        const distTop = centerY;
        const distBottom = height - centerY;

        const minDist = Math.min(distLeft, distRight, distTop, distBottom);

        if (minDist === distLeft) {
          x = margin;
        } else if (minDist === distRight) {
          x = maxX;
        } else if (minDist === distTop) {
          y = margin;
        } else {
          y = maxY;
        }

        return { x, y };
      });
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClick = () => {
    if (wasDragging) {
      setWasDragging(false);

      return;
    }

    toggleChatWindow(!isChatWindowOpen);
  };

  if (isChatWindowOpen) {
    return null;
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={styles.DraggableChatButton}
      style={{
        left: position.x,
        top: position.y,
        width: bubbleSize,
        height: bubbleSize,
        cursor: dragging ? 'grabbing' : 'grab',
        transition: animate ? 'left 0.25s ease-out, top 0.25s ease-out' : 'none',
      }}
    >
      <img src={CaRTBotAgent} alt="CaRT Assistant" />

      {unreadCount > 0 && (
        <div className={styles.Counter}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </div>
      )}
    </div>
  );
};

export default DraggableChatButton;
